import { Button, Spinner } from 'flowbite-react';
import { useEffect, useMemo, useState } from 'react';
import ReactSelect from 'react-select';
import { toast } from 'react-toastify';
import CommonApi from '../../Api/CommonApi';
import PartnerOpportunitiesApi from '../../Api/PartnerOpportunitiesApi';
import { PartnerOpportunityRegionOption } from '../../Models/PartnerOpportunity';
import { State } from '../../Models/State';
import { solidMenuReactSelectProps } from '../../Utils/reactSelectStyles';

interface Props {
  memberId: number;
}

interface RegionSelectOption {
  label: string;
  value: number;
}

interface StateSelectOption {
  label: string;
  value: string;
}

export default function PartnerOpportunityMemberConfiguration({ memberId }: Props) {
  const [regions, setRegions] = useState<PartnerOpportunityRegionOption[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [selectedRegionIds, setSelectedRegionIds] = useState<number[]>([]);
  const [selectedStateCodes, setSelectedStateCodes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      PartnerOpportunitiesApi.getRegions(),
      PartnerOpportunitiesApi.getMemberRegionPreferences(memberId),
      CommonApi.getStates(),
      PartnerOpportunitiesApi.getMemberStatePreferences(memberId),
    ])
      .then(([regionsResponse, regionPrefs, statesResponse, statePrefs]) => {
        setRegions(regionsResponse.data);
        setSelectedRegionIds(regionPrefs.data.regionOptionIds ?? []);
        setStates(statesResponse.data ?? []);
        setSelectedStateCodes(statePrefs.data.stateCodes ?? []);
      })
      .catch((err) => {
        console.log(err);
        toast.error('Unable to load Partner Opportunity Configuration');
      })
      .finally(() => setIsLoading(false));
  }, [memberId]);

  const regionOptions = useMemo<RegionSelectOption[]>(
    () =>
      regions.map((region) => ({
        label: region.name,
        value: region.id,
      })),
    [regions]
  );

  const selectedRegionOptions = useMemo(
    () => regionOptions.filter((option) => selectedRegionIds.includes(option.value)),
    [regionOptions, selectedRegionIds]
  );

  const stateOptions = useMemo<StateSelectOption[]>(
    () =>
      states.map((state) => ({
        label: `${state.name} (${state.code})`,
        value: state.code,
      })),
    [states]
  );

  const selectedStateOptions = useMemo(
    () => stateOptions.filter((option) => selectedStateCodes.includes(option.value)),
    [stateOptions, selectedStateCodes]
  );

  const save = () => {
    setIsSaving(true);
    Promise.all([
      PartnerOpportunitiesApi.saveMemberRegionPreferences({
        memberId,
        regionOptionIds: selectedRegionIds,
      }),
      PartnerOpportunitiesApi.saveMemberStatePreferences({
        memberId,
        stateCodes: selectedStateCodes,
      }),
    ])
      .then(([regionRes, stateRes]) => {
        if (regionRes.data.success && stateRes.data.success) {
          toast.success('Partner Opportunity Configuration saved');
        } else {
          toast.error(
            regionRes.data.message ??
              stateRes.data.message ??
              'Unable to save Partner Opportunity Configuration'
          );
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error('Unable to save Partner Opportunity Configuration');
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <div className='bg-white p-3 my-4'>
      <h2 className='text-gray-900 font-bold text-2xl'>
        Partner Opportunity Configuration
      </h2>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className='relative z-20 space-y-5'>
          <div>
            <p className='text-gray-500 my-2'>
              Select the regions this member wants to receive new Partner Opportunity notifications for.
              Leave blank to receive notifications for all regions.
            </p>
            <ReactSelect
              isMulti
              options={regionOptions}
              value={selectedRegionOptions}
              placeholder='Select interested regions...'
              {...solidMenuReactSelectProps}
              onChange={(items) =>
                setSelectedRegionIds((items ?? []).map((item) => item.value))
              }
            />
          </div>
          <div>
            <p className='text-gray-500 my-2'>
              Select states or provinces for Partner Opportunity alerts. Leave blank to use your
              profile address state/province.
            </p>
            <ReactSelect
              isMulti
              options={stateOptions}
              value={selectedStateOptions}
              placeholder='Select interested states or provinces...'
              {...solidMenuReactSelectProps}
              onChange={(items) =>
                setSelectedStateCodes((items ?? []).map((item) => item.value))
              }
            />
          </div>
          <Button
            className='buttonMain'
            onClick={save}
            disabled={isSaving}
          >
            Save Partner Opportunity Configuration
          </Button>
        </div>
      )}
    </div>
  );
}
